import React, { useState } from 'react';
import {
  FaFilePdf,
  FaFileExcel,
  FaFileImage,
  FaDownload,
  FaFileAlt,
  FaChevronDown,
  FaChevronUp
} from 'react-icons/fa';
import { LocalizedData, RecordItem, TransparencyFile } from './types';

declare global {
  interface Window { NormaVistaData?: LocalizedData[]; }
}

const NormaRecordsByYear: React.FC = () => {
  const localizedData = window.NormaVistaData?.[0] || null;
  if (!localizedData?.data) {
    return <div>Error: datos no disponibles</div>;
  }
  const { site_url, data } = localizedData.data;
  const records = data.records || [];
  if (!records.length) {
    return (
      <>
        <h2 className="nav-title" style={{ fontSize: '1.5rem' }}>Normativas y programas</h2>
        <div className="obligation-card full-width">
          <div className="obligation-card-header">
            <h4 className="obligation-name">No se encontraron registros</h4>
          </div>
        </div>
      </>
    );
  }

  // Agrupar registros por año
  const groupedByYear = records.reduce<Record<string, RecordItem[]>>((acc, rec) => {
    (acc[rec.record_year] = acc[rec.record_year] || []).push(rec);
    return acc;
  }, {});

  const years = Object.keys(groupedByYear)
    .sort((a, b) => Number(b) - Number(a));

  // Estado de pestaña activa y dropdown internos
  const [activeYear, setActiveYear] = useState(years[0]);
  const [openRecords, setOpenRecords] = useState<Record<string, boolean>>({});

  const toggleRecord = (id: string) =>
    setOpenRecords(prev => ({ ...prev, [id]: !prev[id] }));

  const getFileIcon = (name: string) => {
    const ext = name.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return <FaFilePdf />;
    if (['xls','xlsx','csv'].includes(ext || '')) return <FaFileExcel />;
    if (['png','jpg','jpeg','gif'].includes(ext || '')) return <FaFileImage />;
    return <FaFileAlt />;
  };

  const downloadFile = (url: string, name: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderFilesByType = (files: TransparencyFile[]) => {
    if (!files || files.length === 0) {
      return (
        <div className="form-group">
          <h5 className="form-title">Archivos</h5>
          <div className="no-files-message">No hay archivos disponibles</div>
        </div>
      );
    }
    const byType = files.reduce<Record<string, TransparencyFile[]>>((acc, f) => {
      (acc[f.type_name] = acc[f.type_name] || []).push(f);
      return acc;
    }, {});
    return Object.entries(byType).map(([type, list]) => (
      <div key={type} className="form-group">
        <h5 className="form-title">{type}</h5>
        <ul className="files-list">
          {list.map(f => (
            <li key={f.transparency_file_id} className="file-item">
              <a
                href={`${site_url}/wp-content/uploads/${f.file_path}`}
                target="_blank"
                rel="noopener noreferrer"
                className="file-link"
              >
                <span className="file-icon">{getFileIcon(f.file_name)}</span>
                <span className="file-name">{f.file_name}</span>
              </a>
              <button
                className="action-btn download-btn"
                onClick={() =>
                  downloadFile(
                    `${site_url}/wp-content/uploads/${f.file_path}`,
                    f.file_name
                  )
                }
                title="Descargar archivo"
              >
                <FaDownload />
              </button>
            </li>
          ))}
        </ul>
      </div>
    ));
  };

  return (
    <div>
      <h2 className="module-header">Normativas y programas</h2>

      {/* Pestañas de años */}
      <div className="tabs-container">
        {years.map(year => (
          <button
            key={year}
            className={`tab-btn ${activeYear === year ? 'active' : ''}`}
            onClick={() => setActiveYear(year)}
          >
            {year}
          </button>
        ))}
      </div>

      {/* Contenido del año activo */}
      <div className="tab-content">
        {groupedByYear[activeYear].map(rec => {
          const isOpen = !(!!openRecords[rec.record_id]);
          return (
            <div key={rec.record_id} className="file-edit-block">
              <h5
                className="file-title"
                onClick={() => toggleRecord(rec.record_id)}
                style={{ justifyContent: 'space-between', display: 'flex', cursor: 'pointer' }}
              >
                <span>{rec.record_name}</span>
                {isOpen ? <FaChevronUp /> : <FaChevronDown />}
              </h5>
              {isOpen && renderFilesByType(rec.transparency_files)}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default NormaRecordsByYear;
